/* Manages topic section groupings state via Convex user settings.
   Uses optimistic local state to prevent blink on mutation round-trip. */

import { useUserSettings } from "@/hooks/useUserSettings";
import type { SectionGrouping } from "@/lib/defaultSectionGroupings";
import { useCallback, useRef, useState } from "react";

export function useSectionGroupings() {
  const { settings, updateSetting } = useUserSettings();

  const sectionsEnabled = settings?.sectionsEnabled ?? false;
  const serverGroupings: SectionGrouping[] = (settings?.sectionGroupings as SectionGrouping[] | undefined) ?? [];

  const [optimisticGroupings, setOptimisticGroupings] = useState<SectionGrouping[] | null>(null);
  const pendingRef = useRef(0);

  const sectionGroupings = optimisticGroupings ?? serverGroupings;

  const setSectionsEnabled = useCallback(
    (enabled: boolean) => {
      updateSetting("sectionsEnabled", enabled);
    },
    [updateSetting],
  );

  const setSectionGroupings = useCallback(
    (groupings: SectionGrouping[]) => {
      setOptimisticGroupings(groupings);
      pendingRef.current += 1;
      updateSetting("sectionGroupings", groupings);
      // Clear optimistic state after server has likely responded
      setTimeout(() => {
        pendingRef.current -= 1;
        if (pendingRef.current === 0) {
          setOptimisticGroupings(null);
        }
      }, 1500);
    },
    [updateSetting],
  );

  const addSection = useCallback(
    (name: string) => {
      setSectionGroupings([...sectionGroupings, { name, tags: [] }]);
    },
    [sectionGroupings, setSectionGroupings],
  );

  const removeSection = useCallback(
    (index: number) => {
      setSectionGroupings(sectionGroupings.filter((_, i) => i !== index));
    },
    [sectionGroupings, setSectionGroupings],
  );

  const toggleTagInSection = useCallback(
    (sectionIndex: number, tag: string) => {
      const updated = sectionGroupings.map((section, i) => {
        if (i !== sectionIndex) return section;
        const hasTag = section.tags.includes(tag);
        return {
          ...section,
          tags: hasTag ? section.tags.filter((t) => t !== tag) : [...section.tags, tag],
        };
      });
      setSectionGroupings(updated);
    },
    [sectionGroupings, setSectionGroupings],
  );

  return {
    sectionsEnabled,
    sectionGroupings,
    setSectionsEnabled,
    setSectionGroupings,
    addSection,
    removeSection,
    toggleTagInSection,
  };
}
